import { HeartHandshake, Trophy, Building2, Gift, Utensils, GraduationCap, ShieldCheck } from "lucide-react";
import {
  PageHero,
  Prose,
  SectionHeading,
  RelatedLinks,
  TrusteesCallout,
  mailto,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { DonateButton } from "@/components/site/DonateButton";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const WHERE = [
  { Icon: Utensils, title: "Feeds and cares for our horses", body: "Feed, bedding, farrier and vet care keep our horses healthy and happy." },
  { Icon: GraduationCap, title: "Trains our volunteers", body: "Training and support so our team can give riders the best experience." },
  { Icon: ShieldCheck, title: "Keeps our facilities safe", body: "Maintaining a safe, accessible and welcoming centre for everyone." },
];

export default function SupportUs() {
  return (
    <div data-testid="support-us-page">
      <Seo title="Support Us — RDA Glasgow" description="Every gift helps a rider find their confidence, freedom and joy. Donate, fundraise, partner with us or give in kind to support RDA Glasgow." image={ASSETS.therapyCloseup} />
      <PageHero
        title="Support Us"
        subtitle="Every gift helps a rider find their confidence, freedom and joy."
        image={ASSETS.therapyCloseup}
        imageAlt="A rider smiling during a session at RDA Glasgow"
      />

      <Prose>
        <SectionHeading eyebrow="Why your support matters" title="Your support keeps riders riding" />
        <p>
          Providing therapeutic riding takes real resources — caring for our horses,
          supporting our coaches and volunteers, and maintaining safe, accessible
          facilities all year round.
        </p>
        <p>
          As a charity, we rely on the generosity of our community to keep going. Your
          support, in whatever form, directly helps disabled people across Greater
          Glasgow experience the benefits of horses.
        </p>
      </Prose>

      {/* Ways to help */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="Ways to help" title="Choose the way that suits you" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {/* Donate */}
            <Reveal>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft" data-testid="support-card-donate">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal">
                  <HeartHandshake className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-rda-ink">Donate</h3>
                <p className="mt-2 text-sm text-rda-muted flex-1">
                  Online donations are coming soon. For now please email us — every gift
                  helps.
                </p>
                <DonateButton className="mt-5 self-start" withIcon />
              </div>
            </Reveal>

            {/* Fundraise */}
            <Reveal delay={0.07}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft" data-testid="support-card-fundraise">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal">
                  <Trophy className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-rda-ink">Fundraise for us</h3>
                <p className="mt-2 text-sm text-rda-muted flex-1">
                  Take on a sponsored ride, set up workplace giving or run your own event
                  in aid of RDA Glasgow.
                </p>
                <Button asChild variant="outline" className="mt-5 self-start rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold" data-testid="support-fundraise-cta">
                  <a href={mailto("Fundraising enquiry")}>Get in touch</a>
                </Button>
              </div>
            </Reveal>

            {/* Partnerships */}
            <Reveal delay={0.14}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft" data-testid="support-card-partnerships">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-rda-ink">Corporate & community partnerships</h3>
                <p className="mt-2 text-sm text-rda-muted flex-1">
                  Partner with us as a business or community group — from charity of the
                  year to team volunteering.
                </p>
                <Button asChild variant="outline" className="mt-5 self-start rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold" data-testid="support-partnerships-cta">
                  <a href={mailto("Partnership enquiry")}>Get in touch</a>
                </Button>
              </div>
            </Reveal>

            {/* In-kind */}
            <Reveal delay={0.21}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft" data-testid="support-card-inkind">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal">
                  <Gift className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-rda-ink">In-kind gifts</h3>
                <p className="mt-2 text-sm text-rda-muted flex-1">
                  Feed, tack, treats or your time — practical gifts make a real difference
                  to our horses and riders.
                </p>
                <Button asChild variant="outline" className="mt-5 self-start rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold" data-testid="support-inkind-cta">
                  <a href={mailto("In-kind gift enquiry")}>Get in touch</a>
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Where your money goes */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="Where your money goes" title="Your support, put to work" />
          <div className="mt-10 space-y-4">
            {WHERE.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="flex items-start gap-5 rounded-2xl border border-border bg-white p-6 shadow-soft" data-testid={`where-band-${i}`}>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rda-green/15 text-rda-green">
                    <item.Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-rda-ink">{item.title}</h3>
                    <p className="mt-1 text-sm text-rda-muted">{item.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TrusteesCallout />

      <RelatedLinks
        links={[
          { title: "Volunteering", description: "Give your time and change lives.", to: "/volunteering" },
          { title: "About Us", description: "Learn more about the charity you're supporting.", to: "/about" },
          { title: "Contact", description: "Get in touch about supporting our work.", to: "/contact" },
        ]}
      />
    </div>
  );
}
